import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Sparkles, X, Send, Loader2, RotateCcw } from "lucide-react";

const SUGGESTIONS = ["Comment poser un congé pour un agent ?", "Comment est calculé le coût employeur ?", "Comment relancer un email de compte rendu échoué ?"];

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div data-testid={`help-ai-msg-${m.role}`}
      className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[#007AFF] text-white rounded-br-md" : "bg-zinc-800/90 text-zinc-100 rounded-bl-md"}`}>
      {m.content.replace(/\*\*(.+?)\*\*/g, "$1") || <Loader2 className="w-4 h-4 animate-spin" />}
    </div>
  </div>
);

async function streamAsk(body, onEvent) {
  const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
  const res = await fetch(`${API}/help-ai/stream`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) throw new Error("Le service IA est indisponible.");
  const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const chunks = buf.split("\n\n"); buf = chunks.pop();
    chunks.forEach((c) => { if (c.startsWith("data: ")) onEvent(JSON.parse(c.slice(6))); });
  }
}

export const HelpAiWidget = () => {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [sid, setSid] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && !sid && !msgs.length) api.get("/help-ai/latest").then(({ data }) => { setSid(data.session_id); setMsgs(data.messages || []); }).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    const append = (s) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + s }; return c; });
    try {
      await streamAsk({ session_id: sid, message, page: loc.pathname }, (ev) => {
        if (ev.session_id) setSid(ev.session_id);
        if (ev.delta) append(ev.delta);
        if (ev.error) append(ev.error);
      });
    } catch (e) { append(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="help-ai-toggle" aria-label="Aide IA"
        className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 rounded-full bg-[#007AFF] hover:bg-[#0063d1] text-white pl-3.5 pr-4 h-12 shadow-[0_8px_30px_rgba(0,122,255,0.35)] transition-transform duration-200 hover:-translate-y-0.5">
        {open ? <X className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}<span className="text-sm font-medium">Aide IA</span>
      </button>
      {open && (
        <div data-testid="help-ai-panel"
          className="fixed z-[60] bottom-20 right-3 left-3 sm:left-auto sm:right-5 sm:w-[400px] h-[min(560px,calc(100vh-7rem))] flex flex-col rounded-2xl border border-white/10 bg-zinc-950/95 backdrop-blur-xl shadow-2xl animate-in fade-in slide-in-from-bottom-3 duration-200">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div><p className="text-white font-medium flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#007AFF]" /> Aide IA</p>
              <p className="text-[11px] text-zinc-500">Questions sur l'utilisation de l'application</p></div>
            <div className="flex gap-1">
              <button onClick={() => { setSid(""); setMsgs([]); }} title="Nouvelle conversation" data-testid="help-ai-new" className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5"><RotateCcw className="w-4 h-4" /></button>
              <button onClick={() => setOpen(false)} data-testid="help-ai-close" className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="help-ai-messages">
            {!msgs.length && (
              <div className="space-y-2">
                <p className="text-sm text-zinc-400">Bonjour ! Posez votre question sur l'application, par exemple :</p>
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => ask(s)} data-testid="help-ai-suggestion" className="block w-full text-left text-sm text-zinc-200 rounded-lg border border-white/10 px-3 py-2 hover:bg-white/5 transition-colors">{s}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => <Bubble key={i} m={m} />)}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="p-3 border-t border-white/10 flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Votre question…" data-testid="help-ai-input"
              className="flex-1 h-10 rounded-xl bg-zinc-900 border border-zinc-800 px-3 text-sm text-zinc-100 focus:outline-none focus:border-[#007AFF]" />
            <button type="submit" disabled={busy || !text.trim()} data-testid="help-ai-send" className="h-10 w-10 rounded-xl bg-[#007AFF] text-white flex items-center justify-center disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};
